import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Monitor, Smartphone, ArrowRight, Bookmark, Share2, RotateCw, ExternalLink, Trash2, FolderOpen, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { BrandLogo } from "@/components/BrandLogo";
import { PreviewFrame } from "@/components/PreviewFrame";
import { BRAND } from "@/lib/branding";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const normalize = (raw) => {
  let u = (raw || "").trim();
  if (!u) return "";
  if (!/^https?:\/\//i.test(u)) u = "https://" + u;
  return u;
};

export default function Studio() {
  const [input, setInput] = useState("");
  const [activeUrl, setActiveUrl] = useState("");
  const [device, setDevice] = useState("desktop");
  const [loading, setLoading] = useState(false);
  const [frameKey, setFrameKey] = useState(0);
  const [saved, setSaved] = useState([]);
  const [savedShareId, setSavedShareId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("save");
  const [labelInput, setLabelInput] = useState("");
  const [slugInput, setSlugInput] = useState("");
  const [slugStatus, setSlugStatus] = useState("idle"); // idle | checking | available | taken | invalid

  const fetchSaved = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/previews`);
      setSaved(res.data);
    } catch (e) {
      /* silent */
    }
  }, []);

  useEffect(() => { fetchSaved(); }, [fetchSaved]);

  const handleLoad = (e) => {
    e?.preventDefault?.();
    const u = normalize(input);
    if (!u) {
      toast.error("Enter a website URL first");
      return;
    }
    setActiveUrl(u);
    setSavedShareId(null);
    setLoading(true);
    setTimeout(() => setLoading(false), 1200);
  };

  const reload = () => {
    if (!activeUrl) return;
    setFrameKey((k) => k + 1);
    setLoading(true);
    setTimeout(() => setLoading(false), 1000);
  };

  const openDialog = (mode) => {
    if (!activeUrl) {
      toast.error(mode === "save" ? "Load a website before saving" : "Load a website before sharing");
      return;
    }
    let host = "";
    try { host = new URL(activeUrl).hostname.replace(/^www\./, ""); } catch { /* noop */ }
    const derivedSlug = (host.split(".")[0] || "site").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    setLabelInput(host || "Website Preview");
    setSlugInput(derivedSlug);
    setSlugStatus("idle");
    setDialogMode(mode);
    setDialogOpen(true);
  };

  const handleSave = () => openDialog("save");

  const handleShare = async () => {
    if (savedShareId) {
      const link = `${window.location.origin}/${savedShareId}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Share link copied to clipboard");
      } catch { toast.error("Could not copy link"); }
      return;
    }
    openDialog("share");
  };

  // Live slug availability check (debounced)
  useEffect(() => {
    if (!dialogOpen) return;
    const raw = slugInput.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    if (!raw) { setSlugStatus("invalid"); return; }
    setSlugStatus("checking");
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/slug-available/${encodeURIComponent(raw)}`);
        setSlugStatus(res.data.available ? "available" : "taken");
      } catch { setSlugStatus("idle"); }
    }, 400);
    return () => clearTimeout(t);
  }, [slugInput, dialogOpen]);

  const confirmDialog = async () => {
    const cleanSlug = slugInput.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    if (!cleanSlug) { toast.error("Choose a link name (letters, numbers, hyphens)"); return; }
    if (slugStatus === "taken") { toast.error("That link is taken — pick another"); return; }
    try {
      const res = await axios.post(`${API}/previews`, {
        url: activeUrl, device, label: labelInput.trim(), slug: cleanSlug,
      });
      const shareId = res.data.share_id;
      setSavedShareId(shareId);
      await fetchSaved();
      setDialogOpen(false);
      if (dialogMode === "share") {
        const link = `${window.location.origin}/${shareId}`;
        try {
          await navigator.clipboard.writeText(link);
          toast.success("Share link copied to clipboard");
        } catch { toast.success("Saved. Copy the link from Saved."); }
      } else {
        toast.success("Preview saved");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save preview";
      toast.error(msg);
      if (e?.response?.status === 409) setSlugStatus("taken");
    }
  };

  const loadSaved = (p) => {
    setInput(p.url);
    setActiveUrl(p.url);
    setDevice(p.device || "desktop");
    setSavedShareId(p.share_id);
    setLoading(true);
    setTimeout(() => setLoading(false), 1000);
  };

  const deleteSaved = async (shareId, ev) => {
    ev.stopPropagation();
    try {
      await axios.delete(`${API}/previews/${shareId}`);
      setSaved((s) => s.filter((x) => x.share_id !== shareId));
      if (savedShareId === shareId) setSavedShareId(null);
      toast.success("Removed");
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="flex flex-col h-screen relative">
      <div className="grain-overlay" />

      {/* Header / control bar */}
      <header className="sticky top-0 z-50 flex flex-wrap items-center gap-2 sm:gap-3 px-3 sm:px-5 md:px-8 py-3 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="flex items-center gap-4 shrink-0">
          <BrandLogo />
        </div>

        {/* URL form */}
        <form onSubmit={handleLoad} className="order-last basis-full md:order-none md:basis-auto md:flex-1 md:min-w-[220px] flex items-center gap-2" data-testid="url-form">
          <div className="relative flex-1">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Enter a website URL, e.g. stripe.com"
              className="h-9 bg-black/50 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-white/30 focus-visible:border-white/30 rounded-lg pr-4"
              data-testid="url-input"
            />
          </div>
          <Button
            type="submit"
            className="h-9 px-3.5 bg-white text-black hover:bg-zinc-200 rounded-lg font-medium gap-1.5 transition-colors shrink-0"
            data-testid="load-button"
          >
            Load <ArrowRight className="h-4 w-4" />
          </Button>
        </form>

        {/* Device toggle */}
        <div className="flex items-center rounded-lg border border-white/10 bg-black/50 p-1 shrink-0" data-testid="device-toggle">
          <ToggleBtn active={device === "desktop"} onClick={() => setDevice("desktop")} testid="desktop-toggle">
            <Monitor className="h-4 w-4" />
            <span className="hidden lg:inline">Desktop</span>
          </ToggleBtn>
          <ToggleBtn active={device === "mobile"} onClick={() => setDevice("mobile")} testid="mobile-toggle">
            <Smartphone className="h-4 w-4" />
            <span className="hidden lg:inline">Mobile</span>
          </ToggleBtn>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <IconBtn onClick={reload} title="Reload" testid="reload-button">
            <RotateCw className="h-4 w-4" />
          </IconBtn>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="h-9 px-2.5 sm:px-3 inline-flex items-center gap-1.5 sm:gap-2 rounded-lg border border-white/10 bg-black/50 text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors focus-visible:ring-2 focus-visible:ring-white/40 outline-none"
                data-testid="saved-menu-trigger"
              >
                <FolderOpen className="h-4 w-4" />
                <span className="hidden lg:inline">Saved</span>
                <span className="text-white/40">{saved.length}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 bg-[#0a0a0b] border-white/10 text-white p-0" data-testid="saved-menu">
              <div className="px-4 py-3 border-b border-white/10">
                <p className="text-sm font-medium font-display">Saved previews</p>
              </div>
              <div className="max-h-80 overflow-auto py-1">
                {saved.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-white/40 text-center">No saved previews yet</p>
                ) : (
                  saved.map((p) => (
                    <div
                      key={p.share_id}
                      onClick={() => loadSaved(p)}
                      className="group flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 cursor-pointer transition-colors"
                      data-testid={`saved-item-${p.share_id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white truncate">{p.title}</p>
                        <p className="text-xs text-white/40 truncate">{p.url}</p>
                      </div>
                      <button
                        onClick={(e) => deleteSaved(p.share_id, e)}
                        className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-red-400 transition-opacity"
                        data-testid={`delete-saved-${p.share_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            onClick={handleSave}
            className="h-9 px-2.5 sm:px-3 rounded-lg border border-white/10 bg-black/50 text-white/80 hover:bg-white/10 hover:text-white gap-1.5 sm:gap-2 transition-colors"
            data-testid="save-button"
          >
            <Bookmark className="h-4 w-4" />
            <span className="hidden lg:inline">Save</span>
          </Button>
          <Button
            onClick={handleShare}
            className="h-9 px-3 sm:px-4 rounded-lg bg-white text-black hover:bg-zinc-200 gap-1.5 sm:gap-2 font-medium transition-colors"
            data-testid="share-button"
          >
            <Share2 className="h-4 w-4" />
            <span className="hidden sm:inline">Share</span>
          </Button>
        </div>
      </header>

      {/* URL bar strip when active */}
      {activeUrl && (
        <div className="flex items-center gap-2 px-4 md:px-8 py-2 border-b border-white/5 bg-[#080809] text-xs text-white/50" data-testid="active-url-strip">
          <span className="h-2 w-2 rounded-full bg-emerald-400/80" />
          <span className="truncate">{activeUrl}</span>
          <a href={activeUrl} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 hover:text-white transition-colors">
            Open original <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}

      {/* Preview area */}
      <main className="flex-1 relative z-10 flex overflow-hidden dotgrid">
        <PreviewFrame key={frameKey} url={activeUrl} device={device} loading={loading} />
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0b] border-white/10 text-white" data-testid="save-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {dialogMode === "share" ? "Share preview" : "Save preview"}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Name this preview. You'll get a clean link like {window.location.host}/your-site — your client only sees this name and your brand, never the original URL.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <Label htmlFor="label" className="text-white/70 text-sm">Preview name</Label>
            <Input
              id="label"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") confirmDialog(); }}
              placeholder="e.g. Homepage v2"
              className="h-10 bg-black/50 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-1 focus-visible:ring-white/30"
              data-testid="preview-label-input"
            />
          </div>

          <div className="space-y-2 py-1">
            <Label htmlFor="slug" className="text-white/70 text-sm">Custom link</Label>
            <div className={`flex items-center h-10 rounded-md bg-black/50 border overflow-hidden transition-colors ${slugStatus === "taken" || slugStatus === "invalid" ? "border-red-500/50" : slugStatus === "available" ? "border-emerald-500/50" : "border-white/10"}`}>
              <span className="pl-3 pr-1 text-sm text-white/40 whitespace-nowrap select-none">{window.location.host}/</span>
              <input
                id="slug"
                value={slugInput}
                onChange={(e) => setSlugInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") confirmDialog(); }}
                placeholder="my-client-site"
                maxLength={40}
                className="flex-1 h-full bg-transparent text-white placeholder:text-white/30 outline-none pr-3 text-sm"
                data-testid="preview-slug-input"
              />
            </div>
            <p className="text-xs h-4" data-testid="slug-status">
              {slugStatus === "checking" && <span className="text-white/40">Checking availability…</span>}
              {slugStatus === "available" && <span className="text-emerald-400">✓ Available</span>}
              {slugStatus === "taken" && <span className="text-red-400">Already taken — try another</span>}
              {slugStatus === "invalid" && <span className="text-red-400">Use letters, numbers, and hyphens</span>}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-white/70 hover:bg-white/10 hover:text-white" data-testid="dialog-cancel">
              Cancel
            </Button>
            <Button onClick={confirmDialog} disabled={slugStatus === "taken" || slugStatus === "invalid" || slugStatus === "checking"} className="bg-white text-black hover:bg-zinc-200 gap-2 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="dialog-confirm">
              {dialogMode === "share" ? <><Share2 className="h-4 w-4" /> Save & copy link</> : <><Bookmark className="h-4 w-4" /> Save</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ToggleBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-white/40 ${
      active ? "bg-white text-black" : "text-white/60 hover:text-white hover:bg-white/5"
    }`}
  >
    {children}
  </button>
);

const IconBtn = ({ onClick, title, children, testid }) => (
  <button
    onClick={onClick}
    title={title}
    data-testid={testid}
    className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-white/10 bg-black/50 text-white/70 hover:bg-white/10 hover:text-white transition-colors outline-none focus-visible:ring-2 focus-visible:ring-white/40"
  >
    {children}
  </button>
);
